import React, {useEffect, useState} from "react";
import {Container, Text} from "bounce-ui/backoffice";
import {AccountExternal} from "declerations/server/account_models";
import {
  FirstPlan,
  IPlanMatrix,
  PaymentPlanTabMatrix,
} from "../../../../../../../declerations/server/payment_plan_models";
import {PaymentPlanBuilderFrequency, PlanType} from "./utils";
import NiceModal from "@ebay/nice-modal-react";
import FilterOffersDialog from "./FilterOffersDialog";
import {PaymentSchedule} from "./payment-schedule/PaymentSchdule";
import {StyledCard} from "./Components";
import {divide} from "@bounce/shared/helpers";
import {getDefaultValues} from "./defaultValues";
import PlanMatrix from "./plan-matrix/PlanMatrix";
import {isEmpty} from "lodash";
import ConfigurePlan from "./configure-plan/ConfigurePlan";
import {useUtils} from "../../../../../../../contexts";
import {useGetTablePaymentOffers} from "../../../../../../../apis/requests/backoffice";

interface IChooseInstallmentsAmount {
  handleNext: () => void;
  userBalance: AccountExternal;
  selectedPlan: IPlanMatrix | null;
  setSelectedPlan: (planMatrix: IPlanMatrix | null) => void;
  isSupervisorView: boolean;
  resetSelectedPlan: boolean;
  setResetSelectedPlan: (value: boolean) => void;
  isPreview?: boolean;
}

const ChooseInstallmentsAmount = ({
  handleNext,
  userBalance,
  selectedPlan,
  setSelectedPlan,
  isSupervisorView,
  resetSelectedPlan,
  setResetSelectedPlan,
  isPreview = false,
}: IChooseInstallmentsAmount) => {
  const {enqueueSnackbar} = useUtils();
  const [planOffersData, setPlanOffersData] = React.useState<PaymentPlanTabMatrix>();
  const getTablePaymentOffers = useGetTablePaymentOffers({});
  const defaultValues = getDefaultValues(userBalance.total_balance, selectedPlan);
  const [frequency, setFrequency] = useState<PaymentPlanBuilderFrequency>(defaultValues?.frequency);
  const [editMode, setEditMode] = React.useState<boolean>(!selectedPlan);
  const [filteredPlanOffers, setFilteredPlanOffers] = useState<(IPlanMatrix | null)[][]>();
  const [minAmount, setMinAmount] = useState<number>(1);
  const [maxAmount, setMaxAmount] = useState<number>(divide(userBalance.total_balance, 100));
  const [noPlanOffers, setNoPlanOffers] = useState<boolean>(false);
  const [planType, setPlanType] = React.useState<PlanType | undefined>(defaultValues?.planType);

  const fetchPaymentOffers = async ({firstPlan, planType}: {firstPlan: FirstPlan; planType: PlanType}) => {
    try {
      const data = await getTablePaymentOffers.mutateAsync({
        accountId: userBalance.id,
        first_plan: firstPlan,
        plan_type: planType,
      });
      setPlanOffersData(data);
    } catch (error) {
      enqueueSnackbar(<Text size={14}>{"Failed to generate the payment plan options"}</Text>, {
        variant: "error",
      });
    }
  };

  const handleClickFilter = () => {
    NiceModal.show(FilterOffersDialog, {
      totalBalance: userBalance.total_balance,
      allPlanOffers: planOffersData?.[frequency],
      setFilteredPlanOffers,
      minAmount,
      setMinAmount,
      maxAmount,
      setMaxAmount,
      isSupervisorView,
    });
  };

  useEffect(() => {
    if (planOffersData) {
      const hasAnyPlan = planOffersData[frequency].some((row) => row.length > 0);
      setNoPlanOffers(!hasAnyPlan);
    }
  }, [planOffersData]);

  useEffect(() => {
    if (resetSelectedPlan && !noPlanOffers && planOffersData && !isEmpty(planOffersData[frequency])) {
      setSelectedPlan(planOffersData[frequency][0][0]);
    }
  }, [frequency, noPlanOffers, planOffersData]);

  useEffect(() => {
    if (planOffersData) {
      if (!isSupervisorView) {
        const filteredRestrictedPlans = planOffersData[frequency].map((row) => {
          return row.map((plan) => {
            if (!plan) return null;
            if (!plan.restricted) return plan;
            return null;
          });
        });
        setFilteredPlanOffers(filteredRestrictedPlans);
      } else {
        setFilteredPlanOffers(planOffersData[frequency]);
      }
    }
  }, [frequency, isSupervisorView, planOffersData]);

  return (
    <Container fullWidth>
      <ConfigurePlan
        defaultValues={defaultValues}
        userBalance={userBalance}
        fetchPaymentOffers={fetchPaymentOffers}
        setResetSelectedPlan={setResetSelectedPlan}
        isLoadingData={getTablePaymentOffers.isLoading}
        editMode={editMode}
        setEditMode={setEditMode}
        planType={planType}
        setPlanType={setPlanType}
        noPlanOffers={noPlanOffers}
      />
      {noPlanOffers ? (
        <StyledCard>No plan offers available for your configuration</StyledCard>
      ) : (
        <>
          {!editMode && filteredPlanOffers && (
            <PlanMatrix
              userBalance={userBalance}
              selectedPlan={selectedPlan}
              setSelectedPlan={setSelectedPlan}
              handleClickFilter={handleClickFilter}
              filteredPlanOffers={filteredPlanOffers}
              frequency={frequency}
              setFrequency={setFrequency}
              setResetSelectedPlan={setResetSelectedPlan}
            />
          )}
          {!editMode && selectedPlan && (
            <PaymentSchedule
              selectedPlan={selectedPlan}
              setSelectedPlan={setSelectedPlan}
              handleNext={handleNext}
              resetSelectedPlan={resetSelectedPlan}
              isPreview={isPreview}
            />
          )}
        </>
      )}
    </Container>
  );
};

export default ChooseInstallmentsAmount;
